/// <reference types="vite/client" />
import {db} from './db';
import {getAuthToken} from './auth';
import {photoMediaCache} from './photoJournal';

// A gallery must not open dozens of protected reads at once. Hold a slot until
// the response body finishes, and let canceled/obsolete queued reads leave.
const mediaSlots=4,mediaQueue:{start:()=>void;foreground:boolean}[]=[];
let activeMedia=0;
async function mediaRequest<T>(signal:AbortSignal,foreground:boolean,load:()=>Promise<T>):Promise<T>{
 signal.throwIfAborted();
 await new Promise<void>((resolve,reject)=>{
  const start=()=>{signal.removeEventListener('abort',cancel);activeMedia++;resolve();};
  const entry={start,foreground};
  const cancel=()=>{const index=mediaQueue.indexOf(entry);if(index>=0)mediaQueue.splice(index,1);reject(signal.reason);};
  if(activeMedia<mediaSlots)start();else{mediaQueue.push(entry);signal.addEventListener('abort',cancel,{once:true});}
 });
 try{signal.throwIfAborted();return await load();}
 finally{activeMedia--;const index=mediaQueue.findIndex(entry=>entry.foreground);mediaQueue.splice(index<0?0:index,1)[0]?.start();}
}
const retryDelays=[1500,4000,10000,20000];
const retryableMediaError=(error:unknown)=>{
 const status=Number((error as any)?.status||0);
 return [408,429,500,502,503,504].includes(status)||!status&&['TypeError','TimeoutError','AbortError'].includes((error as any)?.name);
};
const retryWait=(delay:number,signal:AbortSignal)=>new Promise<void>((resolve,reject)=>{
 signal.throwIfAborted();const cancel=()=>{clearTimeout(timer);reject(signal.reason);};
 const timer=setTimeout(()=>{signal.removeEventListener('abort',cancel);resolve();},delay);
 signal.addEventListener('abort',cancel,{once:true});
});

export const isProtectedPhotoUrl=(url:string)=>/\/api\/(?:cameras\/admin\/videos\/[a-f0-9-]{36}\/poster|journal\/videos\/[a-f0-9-]{36}\/poster|scouting\/photos\/[^/]+\/download|journal\/(?:photos\/[^/]+|route-photos\/[^/]+\/[^/]+)\/media)(?:\?|$)/.test(url);
export async function fetchPhotoBlob(url:string,signal:AbortSignal,revision='',saved=false):Promise<Blob>{
 const database=db,token=getAuthToken();
 if(!isProtectedPhotoUrl(url))throw new Error('Photo access unavailable.');
 const runtime=(window as any).process?.env?.VITE_API_URL;
 const base=runtime&&!runtime.startsWith('__')?runtime:import.meta.env.VITE_API_URL||'';
 const allowed=new URL(base||window.location.origin,window.location.origin);
 const target=url.startsWith('/')?new URL(`${String(base).replace(/\/+$/,'')}${url}`,window.location.origin):new URL(url,allowed);
 if(target.origin!==allowed.origin)throw new Error('Photo access unavailable.');
 const current=()=>{signal.throwIfAborted();if(database!==db||token!==getAuthToken())throw new Error('Account changed.');};
 // The selected full photo must not wait behind a gallery's thumbnail backlog.
 const loadOnce=()=>mediaRequest(signal,target.searchParams.get('rendition')!=='thumbnail',async()=>{
 current();
 if(!token)throw Object.assign(new Error('Sign in to refresh this photo.'),{status:401});
 const requestSignal=AbortSignal.any([signal,AbortSignal.timeout(15000)]);
 const response=await fetch(target.href,{headers:{Authorization:`Bearer ${token}`},credentials:'omit',cache:'no-store',signal:requestSignal});
 if(signal.aborted||database!==db||token!==getAuthToken())throw new Error('Account changed.');
 if(!response.ok){
  const retryAfter=response.headers.get('Retry-After'),seconds=Number(retryAfter),date=Date.parse(retryAfter||'');
  const retryAfterMs=retryAfter?Math.max(0,Number.isFinite(seconds)?seconds*1000:date-Date.now()):0;
  await response.body?.cancel();
  throw Object.assign(new Error('Photo is no longer available.'),{status:response.status,retryAfterMs});
 }
 const blob=await response.blob();
 if(signal.aborted||database!==db||token!==getAuthToken())throw new Error('Account changed.');
 return blob;
 });
 const load=async()=>{
  for(let attempt=0;;attempt++)try{return await loadOnce();}
  catch(error){
   current();
   if(attempt>=retryDelays.length||!retryableMediaError(error))throw error;
   const delay=Math.max(retryDelays[attempt],Number((error as any)?.retryAfterMs)||0);
   // An unusually long server hold needs a later deliberate retry, rather
   // than an early request or an unbounded background timer.
   if(delay>60000)throw error;
   await retryWait(delay,signal);
  }
 };
 const blob=await photoMediaCache().get(url,revision,load,!saved&&navigator.onLine!==false);
 if(signal.aborted||database!==db||token!==getAuthToken())throw new Error('Account changed.');return blob;
}
